/**
 * MR / BT asistan günlük sayıları
 * — MR_BT ASİSTAN DÖNÜŞÜM TAKİBİ (2).xlsx: «Ekim 2026» sayfası, Asistan Adı Soyadı, 1.10 / Excel sayı 1.1
 * — Eski dosya: Nükleoplasti MR Sayıları, «1 Ekim» metin başlıkları
 */
import * as XLSX from "xlsx";
import { matchAgentByExcelName } from "@/utils/agentHireDateExcelImport";

const TR_MONTHS = {
  ocak: 1, şubat: 2, mart: 3, nisan: 4, mayıs: 5, mayis: 5,
  haziran: 6, temmuz: 7, ağustos: 8, agustos: 8, eylül: 9, eylul: 9,
  ekim: 10, kasım: 11, kasim: 11, aralık: 12, aralik: 12,
};

const cellStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

const normalizeTr = (s) => cellStr(s).toLowerCase()
  .replace(/ı/g, "i").replace(/İ/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g")
  .replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c");

const pad2 = (n) => String(n).padStart(2, "0");

function parseExcelSerialIso(n) {
  if (!Number.isFinite(n) || n < 1000) return null;
  const dc = XLSX.SSF?.parse_date_code?.(n);
  if (dc && dc.y >= 1990 && dc.y <= 2100) {
    return `${dc.y}-${pad2(dc.m)}-${pad2(dc.d)}`;
  }
  const ms = Math.round((n - 25569) * 86400000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export function parseMrCountCell(v) {
  if (v === null || v === undefined || v === "") return null;
  const s = cellStr(v);
  if (s === "-" || s === "—" || s.toLowerCase() === "g") return null;
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

export function parseMrDateHeader(cell, defaultYear, monthHint = null) {
  if (cell instanceof Date && !Number.isNaN(cell.getTime())) {
    return `${cell.getFullYear()}-${pad2(cell.getMonth() + 1)}-${pad2(cell.getDate())}`;
  }
  if (typeof cell === "number" && Number.isFinite(cell)) {
    if (cell > 1000) {
      const iso = parseExcelSerialIso(cell);
      if (iso) return iso;
    }
    const day = Math.floor(cell + 1e-9);
    if (day >= 1 && day <= 31 && monthHint >= 1 && monthHint <= 12) {
      return `${defaultYear}-${pad2(monthHint)}-${pad2(day)}`;
    }
    const asStr = String(cell);
    const dot = asStr.match(/^(\d{1,2})\.(\d{1,2})$/);
    if (dot) {
      const d = parseInt(dot[1], 10);
      const m = parseInt(dot[2], 10);
      if (d >= 1 && d <= 31 && m >= 1 && m <= 12) {
        return `${defaultYear}-${pad2(m)}-${pad2(d)}`;
      }
    }
    return null;
  }
  const s = cellStr(cell);
  if (!s || /^toplam$/i.test(s)) return null;
  const dmy = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${pad2(dmy[2])}-${pad2(dmy[1])}`;
  }
  const dotDm = s.match(/^(\d{1,2})\.(\d{1,2})$/);
  if (dotDm) {
    const day = parseInt(dotDm[1], 10);
    const month = parseInt(dotDm[2], 10);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return `${defaultYear}-${pad2(month)}-${pad2(day)}`;
    }
  }
  const dm = s.match(/^(\d{1,2})\s+(.+)$/i);
  if (dm) {
    const day = parseInt(dm[1], 10);
    const monKey = normalizeTr(dm[2].replace(/\.$/, ""));
    const month = TR_MONTHS[monKey];
    if (month && day >= 1 && day <= 31) {
      return `${defaultYear}-${pad2(month)}-${pad2(day)}`;
    }
  }
  return null;
}

function parseMonthYearTitle(text) {
  const raw = cellStr(text);
  if (!raw) return null;
  const norm = normalizeTr(raw);
  let month = null;
  for (const [key, m] of Object.entries(TR_MONTHS)) {
    if (norm.includes(key)) {
      month = m;
      break;
    }
  }
  if (!month) return null;
  const y = raw.match(/\b(20\d{2})\b/);
  return { month, year: y ? parseInt(y[1], 10) : null };
}

function isMonthTitleLine(line) {
  for (let c = 0; c < Math.min(line.length, 4); c++) {
    if (parseMonthYearTitle(line[c])) return true;
  }
  return false;
}

function inferYearFromRows(rows, headerRowIdx, fallbackYear) {
  for (let r = Math.max(0, headerRowIdx - 4); r < headerRowIdx; r++) {
    const line = rows[r] || [];
    const text = line.map(cellStr).join(" ");
    const y = text.match(/\b(20\d{2})\b/);
    if (y) return parseInt(y[1], 10);
  }
  return fallbackYear;
}

function isAssistantNameHeader(cell) {
  const s = cellStr(cell).toLowerCase();
  return /asistan|temsilci|satış|adı soyad|adi soyad|ad soyad/i.test(s);
}

function isDateHeaderRow(line, lineFormatted, blockContext) {
  let dateCount = 0;
  let hasNameHint = false;
  const year = blockContext?.year ?? 2025;
  const monthHint = blockContext?.month ?? null;
  for (let c = 0; c < line.length; c++) {
    const fmt = lineFormatted?.[c] ?? line[c];
    if (isAssistantNameHeader(fmt)) hasNameHint = true;
    if (parseMrDateHeader(fmt, year, monthHint)) dateCount++;
    else if (parseMrDateHeader(line[c], year, monthHint)) dateCount++;
  }
  return hasNameHint && dateCount >= 3;
}

function findNameColumn(line) {
  for (let c = 0; c < line.length; c++) {
    if (isAssistantNameHeader(line[c])) return c;
  }
  return 1;
}

function inferBlockContext(rows, rowsFmt, headerRowIdx, defaultYear) {
  for (let up = 0; up <= 2; up++) {
    const r = headerRowIdx - up;
    if (r < 0) continue;
    const line = rowsFmt?.[r] ?? rows[r] ?? [];
    for (let c = 0; c < line.length; c++) {
      const ctx = parseMonthYearTitle(line[c]);
      if (ctx) {
        return { month: ctx.month, year: ctx.year ?? inferYearFromRows(rows, headerRowIdx, defaultYear) };
      }
    }
  }
  const titleJoined = (rowsFmt?.[headerRowIdx - 1] ?? rows[headerRowIdx - 1] ?? [])
    .map(cellStr).filter(Boolean).join(" ");
  const fromTitle = parseMonthYearTitle(titleJoined);
  if (fromTitle) {
    return { month: fromTitle.month, year: fromTitle.year ?? inferYearFromRows(rows, headerRowIdx, defaultYear) };
  }
  return { month: null, year: inferYearFromRows(rows, headerRowIdx, defaultYear) };
}

function isDataRowStop(name) {
  if (!name) return true;
  const n = name.toLowerCase();
  return n.includes("toplam") || n === "bt sayısı" || n.includes("uzman");
}

function isGarbageTitleLine(text) {
  if (!text || text.length < 2) return true;
  if (/asistan|temsilci|satış/i.test(text)) return true;
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const numeric = tokens.filter((t) => /^[\d·.,]+$/.test(t)).length;
  return numeric >= Math.max(2, Math.ceil(tokens.length * 0.55));
}

function inferBlockTitle(rows, headerRowIdx) {
  for (let up = 1; up <= 6; up++) {
    const line = rows[headerRowIdx - up] || [];
    const t = line.map(cellStr).filter(Boolean).join(" ");
    if (isGarbageTitleLine(t)) continue;
    if (/mr|nükleo|nukleo|ekim|mart|bt/i.test(t)) return t;
  }
  for (let up = 1; up <= 4; up++) {
    const t = (rows[headerRowIdx - up] || []).map(cellStr).filter(Boolean).join(" ");
    if (!isGarbageTitleLine(t)) return t;
  }
  const dates = (rows[headerRowIdx] || [])
    .map((c) => parseMrDateHeader(c, 2025))
    .filter(Boolean);
  if (dates.length) {
    const start = dates[0];
    const end = dates[dates.length - 1];
    return `${start.slice(8, 10)}.${start.slice(5, 7)} – ${end.slice(8, 10)}.${end.slice(5, 7)}.${end.slice(0, 4)}`;
  }
  return null;
}

export function parseMrAssistantDailySheet(rows, defaultYear = 2025, rowsFmt = null) {
  const blocks = [];
  for (let r = 0; r < rows.length; r++) {
    const line = rows[r] || [];
    const lineFmt = rowsFmt?.[r] ?? line;
    const blockContext = inferBlockContext(rows, rowsFmt, r, defaultYear);
    if (!isDateHeaderRow(line, lineFmt, blockContext)) continue;

    const year = blockContext.year ?? inferYearFromRows(rows, r, defaultYear);
    const monthHint = blockContext.month;
    const nameCol = findNameColumn(lineFmt);
    const dateCols = [];
    for (let c = 0; c < line.length; c++) {
      if (c === nameCol) continue;
      const fmt = lineFmt[c] ?? line[c];
      const date = parseMrDateHeader(fmt, year, monthHint)
        ?? parseMrDateHeader(line[c], year, monthHint);
      if (date) dateCols.push({ col: c, date });
    }
    if (dateCols.length < 2) continue;

    let title = inferBlockTitle(rows, r);
    if (!title && monthHint) {
      const monthLabel = Object.entries(TR_MONTHS).find(([, m]) => m === monthHint)?.[0] ?? "";
      title = `${monthLabel.toUpperCase()} ${year}`.trim();
    }
    title = title || `Satır ${r + 1}`;

    const assistants = [];
    for (let dr = r + 1; dr < rows.length; dr++) {
      const dataLine = rows[dr] || [];
      const dataFmt = rowsFmt?.[dr] ?? dataLine;
      if (isDateHeaderRow(dataLine, dataFmt, inferBlockContext(rows, rowsFmt, dr, defaultYear))) break;
      if (isMonthTitleLine(dataFmt) || isMonthTitleLine(dataLine)) break;

      let name = cellStr(dataFmt[nameCol] ?? dataLine[nameCol]).replace(/\r\n/g, " ").trim();
      if (!name && nameCol > 0) {
        name = cellStr(dataFmt[nameCol + 1] ?? dataLine[nameCol + 1]) || cellStr(dataFmt[1] ?? dataLine[1]);
      }
      name = name.replace(/\r\n/g, " ").trim();
      if (isDataRowStop(name)) break;
      if (name.length < 2) continue;
      if (/^\d+$/.test(name) && cellStr(dataLine[nameCol + 1])) {
        name = cellStr(dataLine[nameCol + 1]).replace(/\r\n/g, " ").trim();
      }

      const daily = [];
      let total = 0;
      for (const { col, date } of dateCols) {
        const net = parseMrCountCell(dataLine[col]);
        if (net === null) continue;
        daily.push({ report_date: date, net_count: net });
        total += net;
      }
      if (daily.length === 0) continue;

      assistants.push({
        assistant_name: name,
        daily,
        total_net: total,
        day_count: daily.length,
      });
    }

    if (assistants.length > 0) {
      const dates = dateCols.map((d) => d.date).sort();
      blocks.push({
        id: `block-${r}`,
        header_row: r + 1,
        title,
        period_start: dates[0],
        period_end: dates[dates.length - 1],
        day_columns: dateCols.length,
        assistants,
        record_count: assistants.reduce((s, a) => s + a.daily.length, 0),
      });
    }
  }
  return blocks;
}

function sheetNameLooksLikeMrDaily(name) {
  const n = cellStr(name);
  if (!n || /^sheet\d*$/i.test(n)) return false;
  if (/mr|nükleoplasti|nukleo|migren|bt sayı|bt sayi/i.test(n)) return true;
  const norm = normalizeTr(n);
  for (const key of Object.keys(TR_MONTHS)) {
    if (norm.includes(key) && /\b20\d{2}\b/.test(n)) return true;
  }
  return false;
}

export function parseMrBtWorkbook(arrayBuffer, options = {}) {
  const defaultYear = options.defaultYear ?? new Date().getFullYear();
  const preferredSheet = options.sheetName ?? null;

  const bytes = arrayBuffer instanceof ArrayBuffer ? new Uint8Array(arrayBuffer) : arrayBuffer;
  const wb = XLSX.read(bytes, { type: "array", cellDates: false, cellNF: true });
  const sheetNames = wb.SheetNames || [];
  if (!sheetNames.length) {
    return { error: "Boş dosya", sheetNames: [], blocks: [], allRecords: [] };
  }

  let targetSheets = preferredSheet && sheetNames.includes(preferredSheet)
    ? [preferredSheet]
    : sheetNames.filter((n) => sheetNameLooksLikeMrDaily(n));

  if (!targetSheets.length) {
    targetSheets = sheetNames.filter((n) => !/^sheet\d*$/i.test(n));
  }
  const sheetsToScan = targetSheets.length ? targetSheets : [sheetNames[0]];

  const blocks = [];
  for (const sheetName of sheetsToScan) {
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
    const rowsFmt = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
    const sheetBlocks = parseMrAssistantDailySheet(rows, defaultYear, rowsFmt).map((b) => ({
      ...b,
      sheet_name: sheetName,
      id: `${sheetName}-${b.id}`,
    }));
    blocks.push(...sheetBlocks);
  }

  blocks.sort((a, b) => (b.period_end || "").localeCompare(a.period_end || ""));

  const allRecords = [];
  blocks.forEach((block) => {
    block.assistants.forEach((a) => {
      a.daily.forEach((d) => {
        allRecords.push({
          sheet_name: block.sheet_name,
          block_id: block.id,
          assistant_name: a.assistant_name,
          report_date: d.report_date,
          net_count: d.net_count,
        });
      });
    });
  });

  return {
    error: blocks.length ? null : "MR tablo başlığı bulunamadı (Asistan + gün sütunları)",
    sheetNames,
    blocks,
    allRecords,
    defaultYear,
  };
}

/** Panel agent eşleştirme (işe giriş import ile aynı mantık) */
export function matchAgentByName(agents, excelName) {
  return matchAgentByExcelName(agents, excelName);
}

export function buildMrImportPreview(parsed, agents, selectedBlockIds, agentOverrides = {}) {
  const idSet = new Set(selectedBlockIds?.length ? selectedBlockIds : parsed.blocks.map((b) => b.id));
  const rows = [];
  const byAssistant = {};

  const resolveAgent = (assistantName) => {
    const overrideId = agentOverrides[assistantName];
    if (overrideId) {
      const found = agents.find((a) => Number(a.id) === Number(overrideId));
      if (found) return found;
    }
    return matchAgentByName(agents, assistantName);
  };

  parsed.blocks.filter((b) => idSet.has(b.id)).forEach((block) => {
    block.assistants.forEach((a) => {
      const agent = resolveAgent(a.assistant_name);
      const key = a.assistant_name;
      if (!byAssistant[key]) {
        byAssistant[key] = {
          assistant_name: a.assistant_name,
          agent_id: agent?.id ?? null,
          agent_name: agent?.ad_soyad || agent?.name || null,
          match_status: agent ? "matched" : "unmatched",
          total_net: 0,
          day_count: 0,
          daily: [],
        };
      } else if (agent?.id && !byAssistant[key].agent_id) {
        byAssistant[key].agent_id = agent.id;
        byAssistant[key].agent_name = agent.ad_soyad || agent.name || null;
        byAssistant[key].match_status = "matched";
      }
      a.daily.forEach((d) => {
        byAssistant[key].daily.push({ ...d, block_id: block.id });
        byAssistant[key].total_net += d.net_count;
        byAssistant[key].day_count += 1;
      });
    });
  });

  const assistants = Object.values(byAssistant).sort((a, b) => a.assistant_name.localeCompare(b.assistant_name, "tr"));
  const matched = assistants.filter((a) => a.agent_id).length;
  const byAgentDay = new Map();
  let rawMatchedRows = 0;
  assistants.forEach((a) => {
    if (!a.agent_id) return;
    a.daily.forEach((d) => {
      const reportDate = cellStr(d.report_date).slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(reportDate)) return;
      rawMatchedRows += 1;
      const agentId = Number(a.agent_id);
      const key = `${agentId}|${reportDate}`;
      byAgentDay.set(key, {
        assistant_name: a.assistant_name,
        agent_id: agentId,
        report_date: reportDate,
        net_count: d.net_count ?? 0,
      });
    });
  });
  rows.push(...byAgentDay.values());
  const totalDaily = assistants.reduce((s, a) => s + (a.daily?.length ?? 0), 0);
  const duplicateInFile = Math.max(0, rawMatchedRows - rows.length);
  return {
    assistants,
    rows,
    allRows: rows,
    matched_count: matched,
    unmatched_count: assistants.length - matched,
    record_count: totalDaily,
    importable_count: rows.length,
    duplicate_in_file_merged: duplicateInFile,
  };
}
